"use client";

import Link from "next/link";
import { useEffect, useState } from "react";
import { Wordmark } from "@/components/Wordmark/Wordmark";

/**
 * Меню по утверждённой структуре (DECISIONS 07.09.2026): Services, Industries,
 * Work, Pricing, Answers плюс кнопка контакта. Здесь стоят только те пункты,
 * у которых уже есть куда вести; Industries и Answers добавляются вместе со
 * своими страницами.
 *
 * Якоря пишутся как `/#...`, а не `#...`: со страницы услуги или работ такого
 * блока на странице нет, и голый якорь никуда не ведёт.
 */
const LINKS = [
  { href: "/services/", label: "Services" },
  { href: "/work/", label: "Work" },
  { href: "/#pricing", label: "Pricing" },
];

export function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  // Lock body scroll while the mobile sheet is open.
  useEffect(() => {
    document.body.style.overflow = open ? "hidden" : "";
    return () => {
      document.body.style.overflow = "";
    };
  }, [open]);

  return (
    <header className="fixed inset-x-0 top-0 z-50">
      <div
        className={`mx-auto flex max-w-7xl items-center justify-between px-5 transition-all duration-500 sm:px-8 ${
          scrolled
            ? "my-2 rounded-full border border-line/80 bg-base/70 py-2.5 backdrop-blur-xl sm:my-3"
            : "border border-transparent py-4"
        }`}
      >
        {/* Без aria-label: подпись «EWEB Studios home» не совпадала с видимым
            текстом логотипа, и проверка доступности это валила. Имя ссылки
            теперь берётся из самого логотипа. */}
        <Link href="/" className="group flex items-center gap-2">
          <Wordmark className="font-display text-lg font-extrabold tracking-tight text-ink" />
          <span className="h-1.5 w-1.5 rounded-full bg-accent transition-transform duration-300 group-hover:scale-150" />
        </Link>

        <nav className="hidden items-center gap-8 md:flex">
          {LINKS.map((l) => (
            <Link
              key={l.href}
              href={l.href}
              className="text-sm text-muted transition-colors hover:text-ink"
            >
              {l.label}
            </Link>
          ))}
        </nav>

        <div className="flex items-center gap-3">
          <a
            href="#contact"
            className="hidden rounded-full border border-line px-5 py-2 text-sm font-medium text-ink transition-colors hover:border-ink/60 hover:bg-surface md:inline-flex"
          >
            Start a project
          </a>

          <button
            type="button"
            onClick={() => setOpen((v) => !v)}
            aria-label={open ? "Close menu" : "Open menu"}
            aria-expanded={open}
            className="relative z-50 flex h-9 w-9 items-center justify-center rounded-full border border-line md:hidden"
          >
            <span className="flex flex-col gap-[5px]">
              <span
                className={`block h-px w-4 bg-ink transition-transform duration-300 ${open ? "translate-y-[3px] rotate-45" : ""}`}
              />
              <span
                className={`block h-px w-4 bg-ink transition-transform duration-300 ${open ? "-translate-y-[3px] -rotate-45" : ""}`}
              />
            </span>
          </button>
        </div>
      </div>

      {/* Mobile sheet */}
      <div
        className={`fixed inset-0 z-40 flex flex-col justify-center gap-2 bg-base px-8 transition-opacity duration-300 md:hidden ${
          open ? "pointer-events-auto opacity-100" : "pointer-events-none opacity-0"
        }`}
      >
        {LINKS.map((l) => (
          <Link
            key={l.href}
            href={l.href}
            onClick={() => setOpen(false)}
            className="font-display text-4xl font-semibold tracking-tight text-ink"
          >
            {l.label}
          </Link>
        ))}
        <a
          href="#contact"
          onClick={() => setOpen(false)}
          className="mt-6 inline-flex w-fit rounded-full bg-accent px-6 py-3 text-sm font-medium text-[#0a0a0b]"
        >
          Start a project
        </a>
      </div>
    </header>
  );
}
